import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import './About.css';
import artistImage from '../assets/images/banner.png';

const About = () => {
  const artistSchema = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "mainEntity": {
      "@type": "Person",
      "name": "Aanishka Chowdhury",
      "jobTitle": "Professional Canvas Artist",
      "description": "Passionate canvas painter specializing in abstract art, portraits, landscapes, and custom commissions. Based in India, bringing imagination to life through colors and canvas.",
      "url": "https://canvaart.in/about",
      "image": "https://canvaart.in/artist-photo.jpg",
      "knowsAbout": [
        "Abstract Art",
        "Portrait Painting",
        "Landscape Art",
        "Custom Canvas Work",
        "Oil Painting",
        "Acrylic Painting"
      ],
      "workLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "IN"
        }
      },
      "alumniOf": {
        "@type": "Organization",
        "name": "Self-taught Professional Artist"
      },
      "award": [
        "500+ Original Artworks Created",
        "200+ Happy Collectors",
        "10+ Years of Artistic Experience"
      ]
    }
  };

  return (
    <div className="about-page">
      <SEO
        title="About the Artist"
        description="Meet Aanishka Chowdhury, the artist behind Canvaart. Discover the story, inspiration, and creative process behind our original canvas paintings and custom artwork commissions."
        keywords="Aanishka Chowdhury artist, about canvaart, canvas artist India, professional painter biography, custom art artist"
        url="/about"
        schema={artistSchema}
      />

      <header className="about-header">
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            About the Artist
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            The story behind the canvas
          </motion.p>
        </div>
      </header>

      <section className="about-content container" aria-label="Artist biography">
        <div
          className="about-intro"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <div className="about-image-wrapper">
            <img
              src={artistImage}
              width={600}
              alt="Aanishka Chowdhury - Professional canvas artist working in studio"
              className="about-artist-image"
              loading="lazy"
            />
            <div className="about-image-caption">
              <span className="caption-name">Aanishka Chowdhury</span>
              <span className="caption-title">Canvas Painting Specialist</span>
            </div>
          </div>

          <div className="about-text-content">
            <h2>Hello, I'm Aanishka</h2>
            <p>
              Welcome to <strong>Canvaart</strong> — my creative sanctuary where emotions meet 
              colors and every brushstroke tells a story. I am a passionate canvas painter 
              based in India, dedicated to transforming visions into breathtaking original artworks.
            </p>
            <p>
              With over <strong>10 years of experience</strong> in various painting techniques 
              including oil, acrylic, and mixed media, I specialize in creating unique pieces 
              that bring life, warmth, and personality to any space.
            </p>
            <p>
              Whether you're drawn to bold abstract compositions, serene landscapes, 
              intimate portraits, or have a completely custom vision in mind, I work 
              closely with every client to ensure each piece captures the exact emotion 
              and story they want to tell.
            </p>
          </div>
        </div>
      </section>
      <section className="about-content container" aria-label="Artist biography">
        <motion.div
          className="about-stats-bar"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="stat-box">
            <span className="stat-number">500+</span>
            <span className="stat-label">Original Artworks</span>
          </div>
          <div className="stat-box">
            <span className="stat-number">200+</span>
            <span className="stat-label">Happy Collectors</span>
          </div>
          <div className="stat-box">
            <span className="stat-number">10+</span>
            <span className="stat-label">Years Experience</span>
          </div>
          <div className="stat-box">
            <span className="stat-number">50+</span>
            <span className="stat-label">Custom Commissions</span>
          </div>
        </motion.div>
      </section>
      <section className="about-content container" aria-label="Artist biography">
        <motion.div
          className="about-specialties"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <h2>My Specialties</h2>
          <div className="specialty-grid">
            <div className="specialty-card">
              <span className="specialty-emoji" role="img" aria-label="Abstract art">🎨</span>
              <h3>Abstract Art</h3>
              <p>Bold, expressive compositions that evoke emotion and add modern energy to any space.</p>
            </div>
            <div className="specialty-card">
              <span className="specialty-emoji" role="img" aria-label="Portrait">👤</span>
              <h3>Portraits</h3>
              <p>Capturing the soul and personality of the subject with lifelike detail and emotional depth.</p>
            </div>
            <div className="specialty-card">
              <span className="specialty-emoji" role="img" aria-label="Landscape">🌄</span>
              <h3>Landscapes</h3>
              <p>Serene natural scenes — mountains, oceans, forests — that bring the outdoors inside.</p>
            </div>
            <div className="specialty-card">
              <span className="specialty-emoji" role="img" aria-label="Custom work">✨</span>
              <h3>Custom Commissions</h3>
              <p>Your vision, my brushes. Bespoke paintings tailored exactly to your preferences and space.</p>
            </div>
          </div>
        </motion.div>
      </section>
      <section className="about-content container" aria-label="Artist biography">
        <motion.div
          className="about-process"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <h2>My Creative Process</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-icon">1</div>
              <h3>Inspiration & Sketch</h3>
              <p>Every painting begins with a spark of inspiration. I sketch the concept, plan the composition, and visualize the color palette before touching the canvas.</p>
            </div>
            <div className="process-step">
              <div className="step-icon">2</div>
              <h3>Layer by Layer</h3>
              <p>Building depth through multiple layers is key. Each layer adds texture, emotion, and dimension — from the base tones to the finest details.</p>
            </div>
            <div className="process-step">
              <div className="step-icon">3</div>
              <h3>Refinement & Details</h3>
              <p>The magic is in the details. Highlights, shadows, and subtle touches bring the painting to life and give it its unique character.</p>
            </div>
            <div className="process-step">
              <div className="step-icon">4</div>
              <h3>Varnish & Delivery</h3>
              <p>A protective varnish ensures longevity. Every piece is carefully packaged and delivered with a certificate of authenticity.</p>
            </div>
          </div>
        </motion.div>
      </section>
      <section className="about-content container" aria-label="Call to action">
        <motion.div
          className="about-cta"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2>Let's Create Something Beautiful Together</h2>
          <p>
            Every blank canvas is an opportunity. Whether you want to own an original piece 
            or commission something uniquely yours, I'd love to hear from you.
          </p>
          <div className="about-cta-buttons">
            <a href="/gallery" className="btn-primary">Explore the Gallery</a>
            <a href="/custom-request" className="btn-secondary">Commission Custom Art</a>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default About;
